import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { Section, SectionHeader } from "@/components/Section";
import { CTASection } from "@/components/CTASection";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Reveal } from "@/components/Reveal";
import { Tilt3D } from "@/components/Tilt3D";
import { TeamSection } from "@/components/TeamSection";
import { SITE, whatsappUrl } from "@/lib/site";

export const Route = createFileRoute("/sobre")({
  head: () => ({
    meta: [
      { title: "Sobre a SVG Multimídia | +8 anos gerando demanda com SEO e IA" },
      { name: "description", content: "Há mais de 8 anos a SVG Multimídia gera demanda para empresas no Brasil e no exterior com SEO, sites de alta performance e atendimento automatizado por IA com o CRM SVG." },
      { property: "og:title", content: "Sobre a SVG Multimídia — Especialistas em SEO e Presença no Google" },
      { property: "og:description", content: "+8 anos, centenas de projetos nacionais e internacionais. Geração de demanda com SEO + automação de atendimento com IA." },
      { property: "og:url", content: "/sobre" },
    ],
    links: [{ rel: "canonical", href: "https://www.svgmultimidia.com.br/sobre" }],
  }),
  component: Sobre,
});

const stats = [
  { n: "+8", l: "anos de atuação", s: "no mercado digital" },
  { n: "+500", l: "projetos entregues", s: "Brasil e exterior" },
  { n: "+30", l: "nichos atendidos", s: "B2B, serviços e varejo" },
  { n: "24/7", l: "atendimento com IA", s: "via CRM SVG" },
];

const pillars = [
  {
    t: "Geração de demanda com SEO",
    d: "Estratégia técnica e de conteúdo para empresas serem encontradas no Google quando o cliente já está pronto para comprar — SEO local, nacional e Ficha do Google otimizada.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-6 w-6"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /><path d="M11 8v3l2 2" /></svg>
    ),
  },
  {
    t: "Automação de atendimento com IA",
    d: "O CRM SVG vende sozinho pelo WhatsApp: qualifica leads, responde dúvidas, agenda visitas e fecha vendas 24 horas por dia com inteligência artificial treinada para o seu negócio.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-6 w-6"><path d="M21 12c0 4.5-4 8-9 8-1.4 0-2.7-.3-3.9-.8L3 21l1.3-4.4C3.5 15.3 3 13.7 3 12c0-4.5 4-8 9-8s9 3.5 9 8Z" /><path d="M8.5 12h7M8.5 9h7M8.5 15h4" /></svg>
    ),
  },
  {
    t: "Sites de alta performance",
    d: "Sites rápidos, com Core Web Vitals impecáveis, prontos para escalar tráfego orgânico e converter visitantes em oportunidades reais.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-6 w-6"><rect x="3" y="4" width="18" height="14" rx="2" /><path d="M3 9h18" /><path d="m9 14 2 2 4-4" /></svg>
    ),
  },
];

const values = [
  { t: "Visão prática", d: "Estratégia que vira execução — não slides bonitos." },
  { t: "Orientada por busca", d: "Cada decisão começa na intenção real de quem pesquisa no Google." },
  { t: "Resultado mensurável", d: "Relatórios claros: posições, tráfego, leads e vendas." },
  { t: "Comunicação transparente", d: "Conversas diretas, sem promessas irreais." },
  { t: "Acompanhamento próximo", d: "Trabalho consultivo, não pacote engessado." },
  { t: "Construção orgânica", d: "Presença que se acumula e fortalece sua marca no tempo." },
];

const milestones = [
  { y: "2017", t: "Fundação da SVG", d: "Início como agência boutique focada em presença digital orgânica." },
  { y: "2019", t: "Especialização em SEO", d: "Consolidação como referência em SEO local e Ficha do Google." },
  { y: "2022", t: "Expansão nacional", d: "Operação atende empresas em todas as regiões do Brasil." },
  { y: "2024", t: "Projetos internacionais", d: "Clientes em Portugal, EUA e América Latina adotam nosso método." },
  { y: "2025", t: "Lançamento do CRM SVG", d: "Plataforma própria com IA para automação de vendas no WhatsApp." },
];

function Sobre() {
  return (
    <>
      <section className="relative overflow-hidden hero-bg">
        <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute -top-32 left-1/2 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-primary/20 blur-[120px]" />
          <div className="absolute bottom-0 right-0 h-[320px] w-[420px] rounded-full bg-accent/20 blur-[100px]" />
        </div>
        <div className="container-svg pt-12 pb-20 md:pt-20 md:pb-28">
          <Breadcrumbs items={[{ label: "Início", to: "/" }, { label: "Sobre" }]} />
          <Reveal>
            <div className="mt-6 max-w-4xl">
              <span className="eyebrow">Quem somos</span>
              <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
                Há <span className="text-gradient">+8 anos</span> transformando buscas e conversas no WhatsApp em <span className="text-gradient">clientes reais</span>
              </h1>
              <p className="mt-6 text-base text-muted-foreground md:text-lg">
                A SVG Multimídia é uma empresa brasileira de tecnologia e marketing especializada em duas frentes que mudam o jogo de qualquer negócio: <strong className="text-foreground">geração de demanda com SEO</strong> e <strong className="text-foreground">automação de atendimento com IA pelo CRM SVG</strong>. Já entregamos centenas de projetos nacionais e internacionais, atendendo desde clínicas e escritórios locais até marcas com operação em vários países.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href={whatsappUrl()} target="_blank" rel="noopener" className="btn-primary">Falar com um especialista</a>
                <Link to="/equipe" className="btn-ghost">Conhecer a equipe</Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Section>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((s, i) => (
            <Reveal key={s.l} delay={(i % 3) as 0 | 1 | 2}>
              <Tilt3D>
                <div className="card-glass text-center">
                  <div className="text-4xl font-extrabold tracking-tight text-gradient md:text-5xl">{s.n}</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">{s.l}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{s.s}</div>
                </div>
              </Tilt3D>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section>
        <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <div>
              <span className="eyebrow">Nossa história</span>
              <h2 className="mt-3 text-3xl font-bold tracking-tight md:text-4xl">Tecnologia, estratégia e proximidade — desde 2017</h2>
              <div className="mt-6 space-y-4 text-muted-foreground md:text-lg">
                <p>
                  Começamos como uma agência boutique apaixonada por SEO. Com o tempo, viramos referência em <strong className="text-foreground">presença orgânica no Google</strong> para empresas de serviço, varejo e B2B — sem depender de mídia paga para crescer.
                </p>
                <p>
                  Em mais de 8 anos de operação, entregamos <strong className="text-foreground">centenas de projetos</strong>: Ficha do Google otimizada, SEO local em dezenas de cidades, SEO nacional para marcas de cobertura ampla e sites com performance de elite.
                </p>
                <p>
                  Em 2025 lançamos o <Link to="/crm-yt" className="text-primary hover:underline">CRM SVG</Link>, nossa plataforma própria com IA que vende sozinha pelo WhatsApp — fechando o ciclo entre <em>ser encontrado</em> e <em>fechar a venda</em>.
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={1}>
            <Tilt3D>
              <ol className="card-glass relative space-y-6 border-l-2 border-primary/30 pl-6">
                {milestones.map((m) => (
                  <li key={m.y} className="relative">
                    <span className="absolute -left-[34px] grid h-6 w-6 place-items-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground ring-4 ring-background">●</span>
                    <div className="text-xs font-semibold uppercase tracking-wider text-primary">{m.y}</div>
                    <div className="mt-1 text-base font-semibold">{m.t}</div>
                    <p className="mt-1 text-sm text-muted-foreground">{m.d}</p>
                  </li>
                ))}
              </ol>
            </Tilt3D>
          </Reveal>
        </div>
      </Section>

      <Section>
        <SectionHeader eyebrow="O que entregamos" title="Duas frentes que sustentam o crescimento do seu negócio" description="Atraímos quem está pronto para comprar e atendemos cada lead sem deixar ninguém esperando." />
        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          {pillars.map((p, i) => (
            <Reveal key={p.t} delay={(i % 3) as 0 | 1 | 2}>
              <Tilt3D>
                <div className="card-glass h-full">
                  <div className="grid h-12 w-12 place-items-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20">{p.icon}</div>
                  <h3 className="mt-4 text-lg font-semibold">{p.t}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{p.d}</p>
                </div>
              </Tilt3D>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section>
        <SectionHeader eyebrow="Como pensamos" title="O que orienta o nosso trabalho" />
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {values.map((v, idx) => (
            <Reveal key={v.t} delay={(idx % 3) as 0 | 1 | 2}>
              <Tilt3D>
                <div className="card-glass">
                  <h3 className="text-lg font-semibold">{v.t}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{v.d}</p>
                </div>
              </Tilt3D>
            </Reveal>
          ))}
        </div>
      </Section>

      <TeamSection />

      <Section>
        <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr] lg:items-stretch">
          <Reveal>
            <div className="flex h-full flex-col justify-between gap-6">
              <div>
                <span className="eyebrow">Onde estamos</span>
                <h2 className="mt-3 text-3xl font-bold tracking-tight md:text-4xl">Nossa base atende o Brasil e o exterior</h2>
                <p className="mt-4 text-muted-foreground md:text-lg">
                  Operamos de forma híbrida com base física no Sul do Brasil e equipe distribuída. Atendemos empresas em todas as regiões brasileiras e clientes internacionais em Portugal, EUA e América Latina.
                </p>
              </div>
              <Tilt3D>
                <div className="card-glass">
                  <div className="text-sm font-semibold text-primary">Contato direto</div>
                  <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                    <li><span className="text-foreground">WhatsApp:</span> {SITE.phoneDisplay}</li>
                    <li><span className="text-foreground">E-mail:</span> {SITE.email}</li>
                    <li><span className="text-foreground">Atendimento:</span> Segunda a sexta · horário comercial</li>
                  </ul>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <a href={whatsappUrl()} target="_blank" rel="noopener" className="btn-primary">Chamar no WhatsApp</a>
                    <Link to="/contato" className="btn-ghost">Formulário de contato</Link>
                  </div>
                </div>
              </Tilt3D>
            </div>
          </Reveal>
          <Reveal delay={1}>
            <div className="h-full overflow-hidden rounded-2xl border border-border bg-secondary/40 shadow-lg">
              <iframe
                title="Localização SVG Multimídia no Google Maps"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3543.5752393475505!2d-53.39296752454501!3d-27.357744976384748!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94fb75495d068541%3A0x3e6375ddb83b2096!2sSVG%20Multim%C3%ADdia%20%7C%20Ag%C3%AAncia%20de%20marketing%20digital%20%7C%20Sites%20%7C%20Gest%C3%A3o%20de%20tr%C3%A1fego!5e0!3m2!1spt-BR!2sbr!4v1782458161332!5m2!1spt-BR!2sbr"
                width="100%"
                height="100%"
                style={{ border: 0, display: "block", minHeight: 420 }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
          </Reveal>
        </div>
      </Section>

      <CTASection />
    </>
  );
}